import { useState } from "react";
import { ChevronDown, ChevronRight, ExternalLink, FileText, RefreshCw, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { useToast } from "@/components/ui/Toast";
import { useShell } from "@/components/Shell";
import { deleteKnowledge, type KnowledgeDoc } from "@/lib/api";
import { relative, absolute } from "@/lib/format";
import { SOURCE_LABEL } from "./index";

// Synced docs link back to the campground's Roverpass owner dashboard —
// not the Roverpass-stored source_url (which points to the public /c/ page).
// Owners edit synced content there; the next sync picks up changes.
const ROVERPASS_OWNER_BASE = "https://www.roverpass.com/owner/campgrounds";

interface DocCardProps {
  doc: KnowledgeDoc;
  onDeleted: (id: string) => void;
}

/**
 * A single owner-authored document. Header shows the title, source type, and
 * timestamps; clicking expands to reveal the raw markdown content. Delete is
 * confirmed inline (a second click) rather than via a modal to keep things flat.
 */
export function DocCard({ doc, onDeleted }: DocCardProps) {
  const toast = useToast();
  const { home } = useShell();
  const slug = home?.campground.slug;
  const [expanded, setExpanded] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const onDelete = async () => {
    if (!confirmingDelete) {
      setConfirmingDelete(true);
      return;
    }
    setDeleting(true);
    try {
      await deleteKnowledge(doc.id);
      onDeleted(doc.id);
    } catch (e) {
      toast.push(e instanceof Error ? e.message : "Failed to delete", "error");
      setDeleting(false);
      setConfirmingDelete(false);
    }
  };

  return (
    <div className="card">
      <div className="flex items-start justify-between gap-4 px-5 py-4">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex min-w-0 flex-1 items-start gap-2 text-left"
        >
          <span className="mt-0.5 shrink-0 text-text-soft">
            {expanded ? <ChevronDown size={14} strokeWidth={1.75} /> : <ChevronRight size={14} strokeWidth={1.75} />}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-medium text-text-strong">
              {doc.title}
            </span>
            <span className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-text-soft">
              <span className="inline-flex items-center gap-1 rounded bg-surface-3 px-1.5 py-0.5 text-2xs text-text-soft">
                <FileText size={10} strokeWidth={1.75} />
                {SOURCE_LABEL[doc.source_type] ?? doc.source_type}
              </span>
              {doc.is_manual ? (
                <span className="inline-flex items-center gap-1 rounded bg-coral-soft px-1.5 py-0.5 text-2xs text-coral-ink">
                  Manual
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 rounded bg-surface-3 px-1.5 py-0.5 text-2xs text-text">
                  <RefreshCw size={10} strokeWidth={1.75} />
                  From Roverpass
                </span>
              )}
              <span className="text-text-faint">·</span>
              <span title={absolute(doc.created_at)} className="cursor-default">
                {doc.is_manual ? "added" : "synced"} {relative(doc.created_at)}
              </span>
              {doc.updated_at !== doc.created_at && (
                <>
                  <span className="text-text-faint">·</span>
                  <span title={absolute(doc.updated_at)} className="cursor-default">
                    updated {relative(doc.updated_at)}
                  </span>
                </>
              )}
              {!doc.is_active && (
                <>
                  <span className="text-text-faint">·</span>
                  <span className="rounded bg-danger-soft px-1.5 py-0.5 text-2xs text-danger">
                    inactive
                  </span>
                </>
              )}
            </span>
          </span>
        </button>

        <div className="flex shrink-0 items-center gap-2">
          {doc.is_manual ? (
            <>
              {confirmingDelete && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setConfirmingDelete(false)}
                  disabled={deleting}
                >
                  Cancel
                </Button>
              )}
              <Button
                variant="danger"
                size="sm"
                onClick={onDelete}
                loading={deleting}
                iconLeft={!deleting && <Trash2 size={12} strokeWidth={1.75} />}
              >
                {confirmingDelete ? "Confirm delete" : "Delete"}
              </Button>
            </>
          ) : (
            slug && (
              <a
                href={`${ROVERPASS_OWNER_BASE}/${slug}/`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-8 items-center gap-1.5 rounded border border-line bg-surface px-3 text-xs font-medium text-text-strong transition-colors hover:bg-surface-2"
              >
                <ExternalLink size={12} strokeWidth={1.75} />
                Edit on Roverpass
              </a>
            )
          )}
        </div>
      </div>

      {expanded && (
        <div className="border-t border-line bg-surface-2 px-5 py-4">
          <p className="whitespace-pre-wrap break-words text-sm text-text">
            {doc.raw_content}
          </p>
        </div>
      )}
    </div>
  );
}
